<template>
  <div class="course-urls-container">
    <Sidebar />
    <div class="content-wrapper">
      <PageHeader 
        title="Course URLs" 
        :showAddButton="true" 
        buttonText="Add New Course URL" 
        @add-clicked="openCreateModal" 
      />

      <AlertMessage 
        v-if="error" 
        type="error" 
        :message="error" 
        @close="error = null" 
      />

      <AlertMessage 
        v-if="successMessage" 
        type="success" 
        :message="successMessage" 
        @close="successMessage = null" 
      />

      <SearchBar 
        v-model="searchQuery"
        v-model:field="searchField"
        :isActive="isSearchActive"
        @search="performSearch"
        @reset="resetSearch"
        @input="handleSearchInput"
      />

      <LoadingSpinner v-if="loading" message="Loading course URLs..." />

      <DataTable 
        v-else-if="hasCoursesData"
        :data="coursesData.data"
        :columns="tableColumns"
        @view="viewCourse"
        @edit="openEditModal"
        @delete="confirmDelete"
        @preview="openPreviewModal"
      />

      <EmptyState 
        v-else
        :icon="isSearchActive ? 'fa-search' : 'fa-photo-video'"
        :title="isSearchActive ? 'No Search Results' : 'No Course URLs Found'"
        :message="isSearchActive ? 'No course URLs match your search criteria. Try different keywords or filters.' : 'Start by adding a new course URL'"
        :showResetButton="isSearchActive"
        @reset="resetSearch"
        @add="openCreateModal"
      />

      <Pagination 
        v-if="hasCoursesData"
        :currentPage="coursesData.current_page"
        :lastPage="coursesData.last_page"
        :hasNextPage="!!coursesData.next_page_url"
        :hasPrevPage="!!coursesData.prev_page_url"
        @page-change="loadPage"
      />

      <!-- Modals -->
      <CourseFormModal
        v-if="showModal"
        :isEditing="isEditing"
        :form="form"
        @update:form="form = $event"
        :validationErrors="validationErrors"
        :formSubmitting="formSubmitting"
        :loadingCourses="loadingCourses"
        @close="closeModal"
        @submit="isEditing ? updateCourse() : createCourse()"
        @load-courses="loadCourses"
        @select-course="selectCourse"
        @upload-success="handleImageUploadSuccess"
        @upload-error="handleUploadError"
        @open-video-uploader="openVideoUploader"
        @remove-video="removeVideo"
      />

      <ConfirmationModal
        v-if="showDeleteModal"
        title="Confirm Deletion"
        message="Are you sure you want to delete this course URL? This action cannot be undone."
        confirmText="Delete"
        :isSubmitting="formSubmitting"
        @confirm="deleteCourse"
        @cancel="showDeleteModal = false"
      />

      <PreviewModal
        v-if="showPreviewModal"
        :url="previewUrl"
        :type="previewType"
        @close="showPreviewModal = false"
      />

      <DetailsModal
        v-if="showDetailsModal"
        :course="currentCourse"
        @close="showDetailsModal = false"
        @edit="openEditModal"
        @preview="openPreviewModal"
      />

      <CoursesSelectionModal
        v-if="showCoursesModal"
        :courses="filteredCourses"
        :loading="loadingCourses"
        :searchQuery="courseSearchQuery"
        @search="filterCourses"
        @select="selectCourse"
        @close="showCoursesModal = false"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted } from 'vue';
import axios from 'axios';
import cloudflareR2Service from '@/services/cloudflareR2';
import Sidebar from './Sidebar.vue';
import PageHeader from './ui/PageHeader.vue';
import AlertMessage from './ui/AlertMessage.vue';
import SearchBar from './ui/SearchBar.vue';
import LoadingSpinner from './ui/LoadingSpinner.vue';
import DataTable from './ui/DataTable.vue';
import EmptyState from './ui/EmptyState.vue';
import Pagination from './ui/Pagination.vue';
import CourseFormModal from './modals/CourseFormModal.vue';
import ConfirmationModal from './ui/ConfirmationModal.vue';
import PreviewModal from './modals/PreviewModal.vue';
import DetailsModal from './modals/DetailsModal.vue';
import CoursesSelectionModal from './modals/CoursesSelectionModal.vue';

export default defineComponent({
  name: 'CourseUrls',
  
  components: {
    Sidebar,
    PageHeader,
    AlertMessage,
    SearchBar,
    LoadingSpinner,
    DataTable,
    EmptyState,
    Pagination,
    CourseFormModal,
    ConfirmationModal,
    PreviewModal,
    DetailsModal,
    CoursesSelectionModal
  },
  
  setup() {
    // State variables
    const loading = ref(true);
    const formSubmitting = ref(false);
    const error = ref(null);
    const successMessage = ref(null);
    const coursesData = ref(null);
    const currentCourse = ref(null);
    
    // Modal control variables
    const showModal = ref(false);
    const showDeleteModal = ref(false);
    const showPreviewModal = ref(false);
    const showDetailsModal = ref(false);
    const showCoursesModal = ref(false);
    const isEditing = ref(false);
    const editIndex = ref(-1);
    const deleteId = ref(null);
    
    // Preview variables
    const previewUrl = ref(null);
    const previewType = ref('image');
    
    // Form data
    const getDefaultForm = () => ({
      course_id: null,
      image_url: '',
      image_url_ar: '',
      downloadable_url: '',
      preview_temp_file: '',
      preview_video_id: '',
      preview_url: '',
      preview_thumbnail_url: ''
    });
    
    const form = reactive(getDefaultForm());
    const validationErrors = reactive({});
    
    // Course selection variables
    const loadingCourses = ref(false);
    const courseSearchQuery = ref('');
    const courses = ref([]);
    const filteredCourses = ref([]);
    
    // Search variables
    const searchQuery = ref('');
    const searchField = ref('all');
    const isSearchActive = ref(false);
    const searchTimeout = ref(null);

    // Computed properties
    const apiBaseUrl = computed(() => 'https://api.kewaar.com/api/admin/course-urls1');
    
    const apiHeaders = computed(() => ({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('token')
    }));

    const hasCoursesData = computed(() => 
      coursesData.value && 
      coursesData.value.data && 
      coursesData.value.data.length > 0
    );

    const tableColumns = computed(() => [
      { key: 'id', label: 'ID' },
      { key: 'course_name', label: 'Course Name' },
      { key: 'image_url', label: 'Image', type: 'image' },
      { key: 'preview_thumbnail_url', label: 'Preview', type: 'image' },
      { key: 'created_at', label: 'Created', formatter: formatDate },
      { key: 'updated_at', label: 'Updated', formatter: formatDate },
      { key: 'actions', label: 'Actions', type: 'actions' }
    ]);
    
    // Methods
    const loadPage = async (page, searchParams = {}) => {
      try {
        loading.value = true;
        
        // Create params object with page number
        const params = { page };
        
        // Add search parameters
        if (searchParams && Object.keys(searchParams).length > 0) {
          Object.assign(params, searchParams);
        }
        
        const response = await axios.get(apiBaseUrl.value, {
          params,
          headers: apiHeaders.value
        });
        
        if (response.data.success) {
          coursesData.value = response.data.data;
          error.value = null;
          
          if (isSearchActive.value && (!coursesData.value.data || coursesData.value.data.length === 0)) {
            error.value = 'No results found for your search. Try different keywords or reset the search.';
          }
        } else {
          error.value = 'Failed to load course data: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        error.value = 'Error fetching courses: ' + err.message;
        console.error('API Error:', err);
      } finally {
        loading.value = false;
      }
    };
    
    const viewCourse = async (id) => {
      try {
        loading.value = true;
        const response = await axios.get(`${apiBaseUrl.value}/${id}`, {
          headers: apiHeaders.value
        });
        
        if (response.data.success) {
          currentCourse.value = response.data.data;
          showDetailsModal.value = true;
          error.value = null;
        } else {
          error.value = 'Failed to load course details: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        error.value = 'Error fetching course details: ' + err.message;
        console.error('API Error:', err);
      } finally {
        loading.value = false;
      }
    };
    
    const validateForm = () => {
      Object.keys(validationErrors).forEach(key => delete validationErrors[key]);
      
      if (!form.course_id) {
        validationErrors.course_id = 'Course ID is required';
      }
      
      if (!form.image_url) {
        validationErrors.image_url = 'English image URL is required';
      }
      
      if (!form.image_url_ar) {
        validationErrors.image_url_ar = 'Arabic image URL is required';
      }
      
      return Object.keys(validationErrors).length === 0;
    };
    
    const createCourse = async () => {
      if (!validateForm()) return;
      
      try {
        formSubmitting.value = true;
        const response = await axios.post(
          apiBaseUrl.value,
          form,
          { headers: apiHeaders.value }
        );
        
        if (response.data.success) {
          successMessage.value = 'Course URL created successfully!';
          closeModal();
          loadPage(coursesData.value?.current_page || 1);
        } else {
          error.value = `Create failed: ${response.data.message || 'Unknown error'}`;
        }
      } catch (error) {
        handleApiError(error, 'Create failed');
      } finally {
        formSubmitting.value = false;
      }
    };
    
    const updateCourse = async () => {
      if (!validateForm()) return;
      
      try {
        formSubmitting.value = true;
        const response = await axios.put(
          `${apiBaseUrl.value}/${form.id}`,
          form,
          { headers: apiHeaders.value }
        );
        
        if (response.data.success) {
          successMessage.value = 'Course URL updated successfully!';
          if (Array.isArray(coursesData.value?.data) && editIndex.value >= 0) {
            coursesData.value.data.splice(editIndex.value, 1, response.data.data);
          }
          closeModal();
        } else {
          error.value = `Update failed: ${response.data.message || 'Unknown error'}`;
        }
      } catch (error) {
        handleApiError(error, 'Update failed');
      } finally {
        formSubmitting.value = false;
      }
    };
    
    const deleteCourse = async () => {
      try {
        formSubmitting.value = true;
        await axios.delete(
          `${apiBaseUrl.value}/${deleteId.value}`,
          { headers: apiHeaders.value }
        );
        
        successMessage.value = 'Course URL deleted successfully!';
        if (coursesData.value?.data) {
          coursesData.value.data.splice(editIndex.value, 1);
        }
        showDeleteModal.value = false;
      } catch (error) {
        handleApiError(error, 'Delete failed');
      } finally {
        formSubmitting.value = false;
      }
    };
    
    const handleApiError = (error, prefix = 'API error') => {
      error.value = `${prefix}: ${error.response?.data?.message || error.message}`;
      console.error('API Error:', error);
      
      // Check for validation errors
      if (error.response?.data?.errors) {
        Object.assign(validationErrors, error.response.data.errors);
      }
    };
    
    // Modal Methods
    const openCreateModal = () => {
      Object.assign(form, getDefaultForm());
      isEditing.value = false;
      showModal.value = true;
      Object.keys(validationErrors).forEach(key => delete validationErrors[key]);
    };
    
    const openEditModal = (course, index) => {
      Object.assign(form, course);
      isEditing.value = true;
      editIndex.value = index !== undefined ? index : -1;
      showModal.value = true;
      Object.keys(validationErrors).forEach(key => delete validationErrors[key]);
    };
    
    const closeModal = () => {
      showModal.value = false;
      isEditing.value = false;
      editIndex.value = -1;
      Object.assign(form, getDefaultForm());
      Object.keys(validationErrors).forEach(key => delete validationErrors[key]);
    };
    
    const confirmDelete = (id, index) => {
      deleteId.value = id;
      editIndex.value = index;
      showDeleteModal.value = true;
    };
    
    const openPreviewModal = (url, type = 'image') => {
      previewUrl.value = url;
      previewType.value = type;
      showPreviewModal.value = true;
    };
    
    // File Upload Methods
    const handleImageUploadSuccess = (field) => {
      successMessage.value = `Image (${field}) uploaded successfully.`;
      setTimeout(() => successMessage.value = null, 3000);
    };
    
    const handleUploadError = (error) => {
      error.value = `Upload failed: ${error.message || 'Unknown error'}`;
      setTimeout(() => error.value = null, 5000);
    };
    
    const openVideoUploader = () => {
      // Create a file input element programmatically
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv';
      
      input.onchange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file);
          const fileUrl = uploadResponse.fileUrl;
          const thumbnailUrl = fileUrl.replace(/\.[^/.]+$/, "") + ".jpg"; // Assuming thumbnail generated
          
          form.preview_url = fileUrl;
          form.preview_video_id = fileUrl.split('/').pop();
          form.preview_thumbnail_url = thumbnailUrl;
          form.preview_temp_file = fileUrl;
          
          // For downloadable URL, use the same if not set
          if (!form.downloadable_url) {
            form.downloadable_url = fileUrl;
          }
          
          successMessage.value = 'Video uploaded successfully!';
          setTimeout(() => successMessage.value = null, 3000);
        } catch (error) {
          error.value = `Video upload failed: ${error.message || 'Unknown error'}`;
          setTimeout(() => error.value = null, 5000);
        }
      };
      
      input.click();
    };
    
    const removeVideo = () => {
      form.preview_url = '';
      form.preview_video_id = '';
      form.preview_thumbnail_url = '';
      form.preview_temp_file = '';
    };
    
    // Utility Methods
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    };
    
    const loadCourses = async () => {
      showCoursesModal.value = true;
      try {
        loadingCourses.value = true;
        const response = await axios.get('https://api.kewaar.com/api/admin/courses2', {
          headers: apiHeaders.value
        });
        
        if (response.data && response.data.data) {
          courses.value = response.data.data;
          filteredCourses.value = [...courses.value];
          error.value = null;
        } else {
          error.value = 'Failed to load course data';
        }
      } catch (err) {
        error.value = 'Error fetching courses: ' + err.message;
        console.error('API Error:', err);
      } finally {
        loadingCourses.value = false;
      }
    };
    
    const filterCourses = () => {
      if (!courseSearchQuery.value) {
        filteredCourses.value = [...courses.value];
        return;
      }
      
      const query = courseSearchQuery.value.toLowerCase();
      filteredCourses.value = courses.value.filter(course => 
        (course.title && course.title.toLowerCase().includes(query)) || 
        (course.id && course.id.toString().includes(query)) ||
        (course.instructor && course.instructor.first_name && 
         course.instructor.first_name.toLowerCase().includes(query))
      );
    };
    
    const selectCourse = (course) => {
      form.course_id = course.id;
      showCoursesModal.value = false;
    };
    
    // Search Methods
    const handleSearchInput = () => {
      isSearchActive.value = searchQuery.value.length > 0;
      
      // Debounce search if typing quickly
      clearTimeout(searchTimeout.value);
      searchTimeout.value = setTimeout(() => {
        if (searchQuery.value.length >= 2) {
          performSearch();
        }
      }, 500);
    };
    
    const clearSearch = () => {
      searchQuery.value = '';
      searchField.value = 'all';
      isSearchActive.value = false;
      loadPage(1);
    };
    
    const resetSearch = () => {
      clearSearch();
    };
    
    const performSearch = () => {
      // Create empty search params object
      const searchParams = {};
      
      // Only search if query is not empty
      if (searchQuery.value && searchQuery.value.trim() !== '') {
        isSearchActive.value = true;
        const query = searchQuery.value.trim();
        
        // Set appropriate search parameter based on selected field
        if (searchField.value === 'all') {
          // General search across all fields
          searchParams.search = query;
        } else {
          // For field-specific search
          searchParams[`search_${searchField.value}`] = query;
        }
        
        // Load first page with search params
        loadPage(1, searchParams);
      } else {
        // Reset search if query is empty
        isSearchActive.value = false;
        loadPage(1);
      }
    };
    
    // Lifecycle hooks
    onMounted(() => {
      loadPage(1);
    });

    return {
      // State
      loading,
      formSubmitting,
      error,
      successMessage,
      coursesData,
      currentCourse,
      
      // Modal state
      showModal,
      showDeleteModal,
      showPreviewModal,
      showDetailsModal,
      showCoursesModal,
      isEditing,
      editIndex,
      deleteId,
      
      // Preview state
      previewUrl,
      previewType,
      
      // Form state
      form,
      validationErrors,
      
      // Course selection state
      loadingCourses,
      courseSearchQuery,
      courses,
      filteredCourses,
      
      // Search state
      searchQuery,
      searchField,
      isSearchActive,
      
      // Computed
      hasCoursesData,
      tableColumns,
      
      // Methods
      loadPage,
      viewCourse,
      createCourse,
      updateCourse,
      deleteCourse,
      openCreateModal,
      openEditModal,
      closeModal,
      confirmDelete,
      openPreviewModal,
      handleImageUploadSuccess,
      handleUploadError,
      openVideoUploader,
      removeVideo,
      formatDate,
      loadCourses,
      filterCourses,
      selectCourse,
      handleSearchInput,
      clearSearch,
      resetSearch,
      performSearch
    };
  }
});
</script>

<style scoped>
.course-urls-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: #f5f7fa;
}

.content-wrapper {
  flex: 1;
  padding: 2rem;
  margin-left: 300px; /* Match sidebar width */
  max-width: 1400px;
}

@media (max-width: 768px) {
  .content-wrapper {
    padding: 1rem;
    margin-left: 0;
  }
}
</style> 